<template>
  <Modal
    v-model="isModalOpen"
    v-if="isModalOpen"
    :size="isMobile ? 'full' : 'lg'"
    :title="title"
    @close="previews.size !== 0 && onDone()"
  >
    <template #header>
      <h2 class="title2-bold">
        {{ title }}
      </h2>
    </template>
    <div v-if="isModalOpen && uppy" class="h-full w-full" :class="{ 'pb-6': previews.size === 0 }">
      <Dashboard ref="dashboard" :uppy="uppy" :props="options" :disabled="disabled" />
    </div>
    <template v-if="previews.size !== 0" #footer>
      <div class="w-full">
        <WButton type="primary" class="w-full" @click="onDone">Done</WButton>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, ref, watch } from 'vue';
  import Modal from '../Modal.vue';
  import { Dashboard } from '@uppy/vue';
  import { DashboardOptions } from '@uppy/dashboard';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import { getSignedUrl, getUppyinstance } from 'src/plugins/uppy';
  import { config } from 'src/utils/config';
  import { ImageSection } from 'src/types/widget';
  import { useVModel } from '@vueuse/core';
  import Uppy, { Meta } from '@uppy/core';
  import WButton from '../Button.vue';

  const props = defineProps<{
    title: string;
    info: string;
    disabled?: boolean;
    images?: ImageSection[];
    modelValue: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'imagesAdded', images: ImageSection[]): void;
    (e: 'update:modelValue', value: boolean): void;
  }>();
  const isModalOpen = useVModel(props, 'modelValue', emit);
  const previews = ref(new Map<string, ImageSection>());
  const dashboard = ref();

  const options = {
    hideCancelButton: true,
    hidePauseResumeButton: true,
    hideRetryButton: true,
    showRemoveButtonAfterComplete: true,
    proudlyDisplayPoweredByUppy: false,
    singleFileFullScreen: false,
    doneButtonHandler: undefined,
    note: 'Images only, up to 10 MB per file',
    showProgressDetails: true,
    hideProgressAfterFinish: true,
  } satisfies DashboardOptions<Meta, Meta>;
  let uppy: Uppy;
  watch(
    isModalOpen,
    async (value) => {
      if (value) {
        uppy = getUppyinstance(props.title.toLowerCase().replaceAll(' ', '_'));
        uppy.on('file-added', (params) => {
          params.meta.key = `private/photos/${config.value.clientId}/${crypto.randomUUID()}-${params.name}`;
        });
        uppy.on('upload-success', async (params) => {
          if (!params) {
            return;
          }
          if (!previews.value.has(params.id)) {
            const signedUrl = await getSignedUrl(String(params.meta.key));
            previews.value.set(params.id, { key: String(params.meta.key), previewUrl: signedUrl });
          }
        });
        uppy.on('file-removed', (params) => {
          if (previews.value.has(params.id)) {
            previews.value.delete(params.id);
          }
        });
        if (props.images?.length) {
          const files = await Promise.all(
            props.images.map(async (file) => {
              const blob = await fetch(file.previewUrl, { method: 'GET', mode: 'cors' }).then((res) => res.blob());
              return {
                name: file.key.replace(`private/photos/${config.value.clientId}/`, ''),
                type: '',
                data: blob, // file blob
                source: 'Local',
                isRemote: false,
              };
            })
          );
          uppy.addFiles(files);
          uppy.getFiles().forEach((file) => {
            uppy.setFileState(file.id, {
              progress: {
                uploadComplete: true,
                uploadStarted: file.data.size,
                bytesTotal: file.data.size,
                bytesUploaded: file.data.size,
              },
            });
          });
        }
        return;
      }
      uppy?.destroy();
    },
    { immediate: true }
  );
  onBeforeUnmount(() => uppy?.destroy());

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isMobile = breakpoints.smaller('lg');

  function onDone() {
    emit('imagesAdded', [...(props.images ? props.images : []), ...previews.value.values()]);
  }
</script>

<style scoped></style>
